// src/component/datatable2/hooks/useFieldDebounce.ts

import { useCallback, useEffect, useRef } from 'react';

/**
 * One debounce timer per key (field name): `schedule(key, fn)` runs fn after
 * `delay` ms unless the same key is scheduled again; `cancel(key)` drops it.
 * Pending timers are cleared on unmount.
 */
export function useFieldDebounce(delay: number) {
    const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

    const cancel = useCallback((key: string) => {
        if (timers.current[key]) {
            clearTimeout(timers.current[key]);
            delete timers.current[key];
        }
    }, []);

    const schedule = useCallback((key: string, run: () => void) => {
        cancel(key);
        timers.current[key] = setTimeout(() => {
            delete timers.current[key];
            run();
        }, delay);
    }, [cancel, delay]);

    useEffect(() => () => {
        Object.values(timers.current).forEach(clearTimeout);
        timers.current = {};
    }, []);

    return { schedule, cancel };
}
